import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * GRI cart add-ons controller.
 *
 * Manages two cart drawer add-ons through ONE serialized reconcile queue so
 * they never issue overlapping cart writes:
 *
 *   1. Free gift  — auto-added when the qualifying subtotal reaches a threshold,
 *                   removed when it drops back below.
 *   2. Shipping protection — a fee line toggled on/off by the customer, added by
 *                   default when "auto-enable" is on. The customer's explicit
 *                   choice is remembered in localStorage.
 *
 * Both add-ons carry a line-item property (`_free_gift` / `_shipping_protection`)
 * and are EXCLUDED from the qualifying subtotal, so adding or removing either one
 * never changes the qualification decision — which is what keeps this convergent.
 *
 * Cart mutations use the theme's own `CartLinesUpdateEvent` contract, so the
 * drawer, the header count and product forms all re-render as usual.
 */

const SOURCE = 'cart-rewards';
const GIFT_PROP = '_free_gift';
const PROTECT_PROP = '_shipping_protection';
const PROTECT_STORAGE = 'gri:ship-protect-v4';
const MAX_PASSES = 8;

let listening = false;
let running = false;
let pending = false;

/* ------------------------------------------------------------------ config */

/** @returns {{ variantIds: string[], threshold: number, blocked: boolean } | null} */
function getGiftConfig() {
  const el = document.querySelector('cart-rewards[data-auto-gift="true"]');
  if (!(el instanceof HTMLElement)) return null;

  const variantIds = (el.dataset.giftVariantIds ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  const threshold = Number(el.dataset.giftThresholdCents);
  if (!variantIds.length || !Number.isFinite(threshold) || threshold <= 0) return null;

  // Set by Liquid when the cart holds a product that cannot carry a free gift.
  const blocked = el.dataset.giftBlocked === 'true';

  return { variantIds, threshold, blocked };
}

/** @returns {{ variantId: string, autoEnable: boolean, blocked: boolean } | null} */
function getProtectConfig() {
  const el = document.querySelector('cart-shipping-protection[data-variant-id]');
  if (!(el instanceof HTMLElement)) return null;

  const variantId = el.dataset.variantId?.trim();
  if (!variantId) return null;

  return {
    variantId,
    autoEnable: el.dataset.autoEnable === 'true',
    // Set by Liquid when the cart holds a product that ships on its own terms.
    blocked: el.dataset.protectBlocked === 'true',
  };
}

/**
 * Reads the remembered choice.
 *
 * An opt-out is stored as `off:<units>` — the number of real product units in
 * the cart when the customer unticked. That stamp is what lets the opt-out
 * expire the next time they add something, and unlike an in-memory counter it
 * survives navigating to another page.
 *
 * @returns {{ mode: 'on' | 'off' | null, units: number | null }}
 */
function readProtection() {
  let raw = null;
  try {
    raw = localStorage.getItem(PROTECT_STORAGE);
  } catch (_) {
    // localStorage unavailable (private mode) — fall back to the default.
  }
  if (raw === 'on') return { mode: 'on', units: null };
  if (raw === 'off') return { mode: 'off', units: null };
  const stamped = /^off:(\d+)$/.exec(raw ?? '');
  if (stamped) return { mode: 'off', units: Number(stamped[1]) };
  return { mode: null, units: null };
}

/**
 * Forgets the remembered choice. Called when the cart empties: an opt-out belongs
 * to the order the customer was building, not to the browser forever. Without
 * this, one untick while testing (or one cautious shopper who later changes
 * their mind) silently disables auto-enable for good, with nothing in the UI to
 * explain why the box never ticks itself again.
 */
function forgetProtection() {
  try {
    localStorage.removeItem(PROTECT_STORAGE);
  } catch (_) {
    // no-op
  }
}

/** @param {string} value 'on', 'off', or 'off:<units>' */
function rememberProtection(value) {
  try {
    localStorage.setItem(PROTECT_STORAGE, value);
  } catch (_) {
    // no-op
  }
}

/* ---------------------------------------------------------------- cart i/o */

/** @returns {Promise<any>} The current cart as JSON. */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/** @returns {string[]} Section ids for every cart-items-component on the page. */
function cartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

/** @returns {EventTarget} An element to dispatch cart events from. */
function dispatcher() {
  return document.querySelector('cart-rewards, cart-shipping-protection') ?? document.body;
}

/**
 * Adds a line to the cart carrying a hidden `_property` flag.
 * Dispatched as 'update' (not 'add') so the drawer never auto-opens for add-ons.
 * @param {string} variantId
 * @param {string} property
 */
async function addLine(variantId, property) {
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  dispatcher().dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const formData = new FormData();
  formData.set('id', variantId);
  formData.set('quantity', '1');
  formData.append(`properties[${property}]`, 'true');
  if (sections.length) {
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);
  }

  try {
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
    const json = await response.json();
    if (json.status) throw new Error(json.description || json.message || 'Add to cart failed');

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: json.sections,
        source: SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Removes a cart line by its line key.
 * @param {string} lineKey
 */
async function removeLine(lineKey) {
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  dispatcher().dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'remove',
      context: 'cart',
      lines: [{ id: lineKey, quantity: 0 }],
      promise: deferred.promise,
    })
  );

  const body = JSON.stringify({
    id: lineKey,
    quantity: 0,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });

  try {
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const json = JSON.parse(await response.text());
    if (json.errors) throw new Error(JSON.stringify(json.errors));

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
      detail: {
        items: json.items,
        itemCount: json.item_count,
        sections: json.sections,
        source: SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/* -------------------------------------------------------------- reconcile */

/**
 * Fetches the cart and performs at most one add-on mutation to move the cart
 * one step toward the correct state.
 * @returns {Promise<boolean>} True if a mutation was performed (caller re-runs).
 */
async function reconcileOnce(seedCart) {
  const giftConfig = getGiftConfig();
  const protectConfig = getProtectConfig();
  if (!giftConfig && !protectConfig) return false;

  // The caller may already hold a fresh cart from the update event it reacted
  // to; going back to the server for it just delays the tick.
  const cart = seedCart || (await fetchCart());

  let qualifying = 0;
  let otherUnits = 0;
  /** @type {any[]} */
  const giftLines = [];
  /** @type {any | null} */
  let protectLine = null;

  for (const item of cart.items) {
    const props = item.properties || {};
    if (props[GIFT_PROP]) {
      giftLines.push(item);
    } else if (props[PROTECT_PROP]) {
      protectLine = item;
    } else {
      qualifying += item.final_line_price;
      otherUnits += item.quantity;
    }
  }

  // 1) Free gifts. Reconcile toward the configured set (up to two products),
  //    one mutation per pass so the outer loop stays convergent.
  if (giftConfig) {
    const qualifies = !giftConfig.blocked && qualifying >= giftConfig.threshold;
    if (qualifies) {
      // Drop any gift line that is no longer a configured gift.
      const stray = giftLines.find((line) => !giftConfig.variantIds.includes(String(line.variant_id)));
      if (stray) {
        await removeLine(stray.key);
        return true;
      }
      // Add the first configured gift that isn't in the cart yet.
      const presentIds = giftLines.map((line) => String(line.variant_id));
      const missing = giftConfig.variantIds.find((id) => !presentIds.includes(id));
      if (missing) {
        await addLine(missing, GIFT_PROP);
        return true;
      }
    } else if (giftLines.length) {
      // Below the threshold, or blocked by a product in the cart — remove the
      // gifts one at a time.
      await removeLine(giftLines[0].key);
      return true;
    }
  }

  // 2) Shipping protection. Never protect a cart with no real items.
  if (protectConfig) {
    let choice = readProtection();

    if (otherUnits === 0) {
      // Cart emptied: the previous order is done or abandoned, so the next one
      // starts from the merchant's default again.
      forgetProtection();
      choice = { mode: null, units: null };
    } else if (choice.mode === 'off') {
      if (choice.units === null) {
        // Freshly unticked. Stamp it with the cart size so we can tell later
        // whether the shopper has added anything since.
        rememberProtection(`off:${otherUnits}`);
        choice.units = otherUnits;
      } else if (otherUnits > choice.units) {
        // They added another product. That restarts the decision -- otherwise a
        // single untick silences auto-enable for the life of the browser, and
        // nothing on screen explains why the box never ticks again. Removing
        // items is deliberately NOT treated the same, so turning protection off
        // and then editing quantities down still respects the choice.
        forgetProtection();
        choice = { mode: null, units: null };
      }
    }

    const wanted = choice.mode === 'on' ? true : choice.mode === 'off' ? false : protectConfig.autoEnable;
    const desired = !protectConfig.blocked && otherUnits > 0 && wanted;
    const correct = protectLine && String(protectLine.variant_id) === protectConfig.variantId;
    if (desired && !correct) {
      if (protectLine) await removeLine(protectLine.key);
      await addLine(protectConfig.variantId, PROTECT_PROP);
      return true;
    }
    if (!desired && protectLine) {
      await removeLine(protectLine.key);
      return true;
    }
  }

  return false;
}

/**
 * Serialized entry point. Coalesces overlapping requests and keeps running until
 * the cart is fully reconciled (bounded by MAX_PASSES as a safety net).
 */
async function requestReconcile(seedCart) {
  if (running) {
    pending = true;
    return;
  }
  running = true;
  try {
    let passes = 0;
    let mutated = false;
    do {
      pending = false;
      // Only the first pass can trust the seed; after a mutation it is stale.
      mutated = await reconcileOnce(passes === 0 ? seedCart : undefined);
      passes += 1;
    } while ((pending || mutated) && passes < MAX_PASSES);
  } catch (error) {
    console.warn('[cart-rewards] reconcile failed:', error);
  } finally {
    running = false;
  }
}

/* ------------------------------------------------------------- listeners */

/** @param {CartLinesUpdateEvent} event */
function onCartLinesUpdate(event) {
  const promise = /** @type {any} */ (event).promise;

  if (promise && typeof promise.then === 'function') {
    promise
      .then((/** @type {any} */ resolved) => {
        // Ignore the events we dispatch ourselves — the cart is already correct.
        if (resolved?.detail?.source === SOURCE) return;
        const items = resolved?.detail?.items;
        requestReconcile(Array.isArray(items) ? { items } : undefined);
      })
      .catch(() => {
        // A failed mutation may have left the cart in a new state — re-check.
        requestReconcile();
      });
  } else {
    requestReconcile();
  }
}

function startListening() {
  if (listening) return;
  listening = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
}

/* ------------------------------------------------------------- elements */

/**
 * Free-gift / rewards element. Ensures the listener is attached and runs one
 * reconcile on initial load (covers carts that already qualify).
 */
class CartRewards extends HTMLElement {
  connectedCallback() {
    startListening();
    requestAnimationFrame(() => requestReconcile());
  }
}

/**
 * Shipping-protection toggle element. Same reconcile queue; additionally wires
 * the switch so toggling remembers the choice and updates the cart.
 */
class CartShippingProtection extends HTMLElement {
  connectedCallback() {
    startListening();

    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('.ship-protect__input'));
    if (input) input.addEventListener('change', this.#onToggle);

    requestAnimationFrame(() => requestReconcile());
  }

  /** @param {Event} event */
  #onToggle = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.currentTarget);
    rememberProtection(input.checked ? 'on' : 'off');
    // Lock the control until the re-render replaces it, to avoid double-clicks.
    input.disabled = true;
    requestReconcile();
  };
}

if (!customElements.get('cart-rewards')) {
  customElements.define('cart-rewards', CartRewards);
}
if (!customElements.get('cart-shipping-protection')) {
  customElements.define('cart-shipping-protection', CartShippingProtection);
}
